import { useEffect, useState } from "react";
import { api } from "../../api";
import { Brand } from "../../components/Brand";
import {
  confirmPasswordReset,
  signOutFirebase,
  verifyPasswordResetCode,
} from "../../lib/firebaseAuth";
import { passwordChangeEnabled } from "../../lib/passwordChangeFlag";
import { reportSwallowed } from "../../lib/reportSwallowed";
import {
  maskEmail,
  MAX_PASSWORD_LENGTH,
  PASSWORD_HINT,
  passwordProblem,
  PASSWORD_TOO_SHORT,
  RESET_UNREACHABLE,
} from "../../lib/passwordPolicy";
import { readAuthAction, type AuthActionQuery } from "./authActionQuery";
import { ResetPasswordDialog } from "./ResetPasswordDialog";
import {
  goToSignIn,
  PASSWORD_CHANGED,
  stashSignInNotice,
} from "./signInNotice";

const BAD_LINK = "This link has expired or was already used.";

const fieldClass =
  "w-full rounded border border-line bg-canvas px-3 py-2 text-sm text-fg outline-none focus:border-muted";

type Phase =
  | { kind: "checking" }
  | { kind: "ready"; email: string }
  | { kind: "bad"; email: string | null }
  | { kind: "offline" };

type SaveResult =
  { kind: "stay"; message: string } | { kind: "bad" } | { kind: "left" };

export function AuthAction() {
  const [query] = useState(readAuthAction);
  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState<Phase>({ kind: "checking" });
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    let live = true;
    void resolveAction(query).then((next) => {
      if (live) setPhase(next);
    });
    return () => {
      live = false;
    };
  }, [query, attempt]);

  const knownEmail = phase.kind === "bad" ? (phase.email ?? "") : "";

  return (
    <div className="flex h-full items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-sm space-y-3 rounded-lg border border-line bg-surface p-6 shadow-sm">
        <Brand size="lg" />
        {phase.kind === "checking" && (
          <p className="text-sm text-faint">Loading…</p>
        )}
        {phase.kind === "ready" && (
          <NewPasswordForm
            email={phase.email}
            code={query.oobCode}
            onBad={() => setPhase({ kind: "bad", email: phase.email })}
          />
        )}
        {phase.kind === "bad" && (
          <BadLink onSendNew={() => setResetOpen(true)} />
        )}
        {phase.kind === "offline" && (
          <Offline onRetry={() => setAttempt((n) => n + 1)} />
        )}
      </div>
      {resetOpen && (
        <ResetPasswordDialog
          email={knownEmail}
          onClose={() => setResetOpen(false)}
          onBack={() => goToSignIn()}
          onSend={
            passwordChangeEnabled()
              ? (address) => api.requestPasswordReset(address)
              : undefined
          }
        />
      )}
    </div>
  );
}

async function resolveAction(query: AuthActionQuery): Promise<Phase> {
  if (query.mode !== "resetPassword" || query.oobCode === "")
    return { kind: "bad", email: null };
  try {
    const email = await verifyPasswordResetCode(query.oobCode);
    return { kind: "ready", email };
  } catch (err) {
    if (isNetwork(err)) return { kind: "offline" };
    return { kind: "bad", email: null };
  }
}

function NewPasswordForm({
  email,
  code,
  onBad,
}: {
  email: string;
  code: string;
  onBad: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const local = passwordProblem(password, confirm);
    if (local) {
      setError(local);
      return;
    }
    setBusy(true);
    setError(null);
    const result = await savePassword(code, password, email);
    if (result.kind === "bad") {
      onBad();
      return;
    }
    if (result.kind === "stay") {
      setError(result.message);
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-3"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <h2 className="text-sm font-medium text-fg">Set a new password</h2>
      <p className="text-xs text-muted">For {maskEmail(email)}</p>
      <PasswordField
        id="new-password"
        label="New password"
        value={password}
        onChange={setPassword}
      />
      <PasswordField
        id="confirm-password"
        label="Confirm new password"
        value={confirm}
        onChange={setConfirm}
      />
      <p id="new-password-hint" className="text-[10px] text-faint">
        {PASSWORD_HINT}
      </p>
      {error && (
        <p role="alert" className="text-xs text-red-400">
          {error}
        </p>
      )}
      <button
        disabled={busy}
        className="w-full rounded bg-accent px-3 py-2 text-sm font-medium text-canvas hover:bg-accent-hover disabled:opacity-60"
      >
        Save password
      </button>
    </form>
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block space-y-1 text-xs text-muted" htmlFor={id}>
      {label}
      <input
        id={id}
        className={fieldClass}
        type="password"
        autoComplete="new-password"
        maxLength={MAX_PASSWORD_LENGTH}
        aria-describedby="new-password-hint"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function BadLink({ onSendNew }: { onSendNew: () => void }) {
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-medium text-fg">Set a new password</h2>
      <p className="text-xs text-red-400">{BAD_LINK}</p>
      <button
        type="button"
        className="w-full rounded bg-accent px-3 py-2 text-sm font-medium text-canvas hover:bg-accent-hover"
        onClick={onSendNew}
      >
        Send a new link
      </button>
    </div>
  );
}

function Offline({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-red-400">{RESET_UNREACHABLE}</p>
      <button
        type="button"
        className="w-full text-center text-xs text-muted hover:text-fg"
        onClick={onRetry}
      >
        Try again
      </button>
    </div>
  );
}

async function savePassword(
  code: string,
  password: string,
  email: string,
): Promise<SaveResult> {
  try {
    await confirmPasswordReset(code, password);
  } catch (err) {
    if (isWeakPassword(err))
      return { kind: "stay", message: PASSWORD_TOO_SHORT };
    if (isNetwork(err)) return { kind: "stay", message: RESET_UNREACHABLE };
    return { kind: "bad" };
  }
  // The cutoff route holds every answer for the same interval. Wait for it
  // so leaving the page does not drop the request. Any result uses this
  // same success screen and does not say whether the account was found.
  await revokeOtherSessions(email);
  // Leave even if the local sign-out fails. The reload drops the code, which
  // exists only in memory.
  await signOutFirebase().catch(() => undefined);
  stashSignInNotice(PASSWORD_CHANGED);
  goToSignIn();
  return { kind: "left" };
}

async function revokeOtherSessions(email: string): Promise<void> {
  try {
    const result = await api.completePasswordReset(email);
    if (!result.ok) logCompleteFailure(result.status);
  } catch {
    logCompleteFailure(null);
  }
}

function logCompleteFailure(status: number | null): void {
  reportSwallowed(
    "password reset complete",
    new Error(status === null ? "no-response" : `http ${status}`),
  );
}

function isNetwork(err: unknown): boolean {
  const code = firebaseCode(err);
  return code === "auth/network-request-failed" || code === "auth/timeout";
}

function isWeakPassword(err: unknown): boolean {
  const code = firebaseCode(err);
  return (
    code === "auth/weak-password" ||
    code === "auth/password-does-not-meet-requirements"
  );
}

function firebaseCode(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const code = (err as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}
